import './SignUp.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiLock } from 'react-icons/fi';
import TermsAgreement from './TermsAgreement';
import AddressDaum from './AddressDaum';
import { checkSignupId, isValidSignupId, normalizeSignupCode, submitSignup, validateSignup, verifySignupCode } from './signupApi';

const FIXED_CERT_CODE = 'FD2001';
const initialInput = {
  certCode: 'FD2001', kind: '', userid: '', username: '', email: '',
  phone: '', phone_mom: '', address: '', detailAddress: '', zipcode: '',
  password: '', confirmPassword: '',
};

export default function SignUp() {
  const navigate = useNavigate();
  const [input, setInput] = useState(initialInput);
  const inputRef = useRef(initialInput);
  const [verifiedCode, setVerifiedCode] = useState('');
  const [codeMessage, setCodeMessage] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [checkedId, setCheckedId] = useState(null);
  const [idMessage, setIdMessage] = useState('');
  const [checkingId, setCheckingId] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submitLock = useRef(false);
  const mounted = useRef(false);
  const codeRequest = useRef(0);
  const idRequest = useRef(0);
  const feedbackRef = useRef(null);

  const checkCode = useCallback(async () => {
    const certCode = normalizeSignupCode(inputRef.current.certCode);
    const request = ++codeRequest.current;
    idRequest.current += 1;
    setVerifiedCode('');
    setCheckedId(null);
    setIdMessage('');
    setCheckingId(false);
    setCodeMessage('');
    if (!certCode || certCode.length > 50 || (FIXED_CERT_CODE && certCode !== FIXED_CERT_CODE)) {
      setCodeMessage('인증코드를 확인해 주세요.');
      setCheckingCode(false);
      return;
    }
    setCheckingCode(true);
    try {
      const result = await verifySignupCode(certCode);
      if (!mounted.current || request !== codeRequest.current || normalizeSignupCode(inputRef.current.certCode) !== certCode) return;
      setVerifiedCode(certCode);
      setCodeMessage(result.name ? result.name + ' · 인증코드 확인 완료' : '인증코드 확인 완료');
    } catch (err) {
      if (mounted.current && request === codeRequest.current) setCodeMessage(err.message);
    } finally {
      if (mounted.current && request === codeRequest.current) setCheckingCode(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    checkCode();
    return () => {
      mounted.current = false;
      codeRequest.current += 1;
      idRequest.current += 1;
    };
  }, [checkCode]);

  useEffect(() => {
    if (error) feedbackRef.current?.focus();
  }, [error]);

  const handleChange = (event) => {
    if (submitLock.current) return;
    const { name, value } = event.target;
    if (name === 'certCode' && FIXED_CERT_CODE) return;
    const next = { ...inputRef.current, [name]: value };
    inputRef.current = next;
    setInput(next);
    setError('');
    if (name === 'certCode') {
      codeRequest.current += 1;
      setVerifiedCode('');
      setCodeMessage('');
      setCheckingCode(false);
    }
    if (name === 'userid' || name === 'certCode') {
      idRequest.current += 1;
      setCheckedId(null);
      setIdMessage('');
      setCheckingId(false);
    }
  };

  const handleCheckId = async () => {
    if (submitLock.current || checkingId) return;
    const certCode = normalizeSignupCode(inputRef.current.certCode);
    const id = inputRef.current.userid.trim();
    if (verifiedCode !== certCode) { setIdMessage('먼저 인증코드를 확인해 주세요.'); return; }
    if (!isValidSignupId(id)) {
      setIdMessage('아이디는 공백 없이 4~30자로 입력해 주세요.');
      return;
    }
    const request = ++idRequest.current;
    setCheckingId(true);
    setCheckedId(null);
    setIdMessage('');
    try {
      const available = await checkSignupId(certCode, id);
      if (!mounted.current || request !== idRequest.current || inputRef.current.userid.trim() !== id || normalizeSignupCode(inputRef.current.certCode) !== certCode) return;
      setCheckedId({ certCode, id, available });
      setIdMessage(available ? '사용 가능한 아이디입니다.' : '이미 사용 중인 아이디입니다.');
    } catch (err) {
      if (mounted.current && request === idRequest.current) setIdMessage(err.message);
    } finally {
      if (mounted.current && request === idRequest.current) setCheckingId(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLock.current) return;
    const snapshot = { ...inputRef.current, certCode: normalizeSignupCode(inputRef.current.certCode) };
    const message = validateSignup(snapshot, { verifiedCode, checkedId, agreedTerms, agreedPrivacy, fixedCertCode: FIXED_CERT_CODE });
    if (message) { setError(message); return; }
    submitLock.current = true;
    setSubmitting(true);
    setError('');
    try {
      const result = await submitSignup(snapshot);
      if (!mounted.current) return;
      if (result.success === 'exist') {
        setCheckedId(null);
        setIdMessage('이미 사용 중인 아이디입니다.');
        setError('이미 존재하는 아이디입니다. 다른 아이디로 중복확인해 주세요.');
        return;
      }
      navigate('/login', { replace: true, state: { signupCertCode: snapshot.certCode, signupLoginId: snapshot.userid.trim() } });
    } catch (err) {
      if (mounted.current) {
        setError(err.message);
        setCheckedId(null);
        setIdMessage('처리 결과를 확인하려면 아이디 중복확인을 다시 해 주세요.');
      }
    } finally {
      if (mounted.current) { submitLock.current = false; setSubmitting(false); }
    }
  };

  const handleAddressChange = (address, zipcode) => {
    if (submitLock.current) return;
    const next = { ...inputRef.current, address, zipcode, detailAddress: '' };
    inputRef.current = next;
    setInput(next);
  };

  return (
    <main className="signup-page">
      <div className="signup-shell">
        <header className="signup-header">
          <div className="signup-topbar">
            <button type="button" className="signup-back" onClick={() => navigate('/login')} disabled={submitting} aria-label="로그인으로 돌아가기"><FiArrowLeft aria-hidden="true" /></button>
            <span className="signup-brand">AI K-EDU <span>TALK</span></span>
          </div>
          <h1 id="signup-title">회원가입</h1>
          <p className="signup-intro">하나의 계정으로 학습과 AI 서비스를 이용하세요.</p>
        </header>
          <form onSubmit={handleSubmit} noValidate aria-busy={submitting} aria-labelledby="signup-title">
            <fieldset className="signup-fields" disabled={submitting}>
              <section className="signup-code-card" aria-label="서비스 인증코드">
                    <div className="signup-code-heading">
                      <label htmlFor="certCode">인증코드</label>
                      <span className="signup-fixed-badge"><FiLock aria-hidden="true" />고정</span>
                    </div>
                    <input id="certCode" name="certCode" value={FIXED_CERT_CODE} disabled autoComplete="off" aria-describedby="signup-code-status" />
                    <p id="signup-code-status" className="signup-code-status" role="status">{verifiedCode && <FiCheckCircle aria-hidden="true" />}{checkingCode ? '인증코드를 확인하고 있습니다.' : codeMessage}</p>
                    {!checkingCode && !verifiedCode && <Button type="button" className="signup-retry" onClick={checkCode}>인증코드 다시 확인</Button>}
              </section>
              <section className="signup-card" aria-labelledby="signup-account-title">
                <div className="signup-section-heading"><h2 id="signup-account-title">계정 정보</h2><span>모두 필수 항목</span></div>
                <div className="signup-section-fields">
                  <div className="signup-field">
                    <label htmlFor="kind">회원유형</label>
                    <select id="kind" name="kind" value={input.kind} onChange={handleChange}>
                      <option value="">학생 또는 선생님을 선택해 주세요</option>
                      <option value="1">학생</option>
                      <option value="2">선생님</option>
                    </select>
                  </div>
                  <div className="signup-field">
                    <label htmlFor="userid">아이디</label>
                    <div className="signup-inline-field">
                      <input type="text" id="userid" name="userid" value={input.userid} onChange={handleChange} maxLength={30} autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="아이디 입력" aria-describedby="signup-id-hint signup-id-status" />
                      <Button type="button" className="signup-secondary" onClick={handleCheckId} disabled={checkingCode || checkingId || !verifiedCode}>{checkingId ? '확인 중…' : '중복확인'}</Button>
                    </div>
                    <p id="signup-id-hint" className="signup-hint">공백 없이 4~30자로 입력해 주세요.</p>
                    <p id="signup-id-status" className="signup-field-status" role="status">{checkingId ? '아이디를 확인하고 있습니다.' : idMessage}</p>
                  </div>
                  <div className="signup-field">
                    <label htmlFor="username">이름</label>
                    <input type="text" id="username" name="username" value={input.username} onChange={handleChange} maxLength={20} autoComplete="name" placeholder="이름을 입력해 주세요" />
                  </div>
                  <div className="signup-field">
                    <label htmlFor="password">비밀번호</label>
                    <input type="password" id="password" name="password" value={input.password} onChange={handleChange} minLength={6} maxLength={31} autoComplete="new-password" placeholder="6자 이상 입력" aria-describedby="signup-password-hint" />
                    <p id="signup-password-hint" className="signup-hint">영문·숫자는 최대 31자까지 입력할 수 있어요.</p>
                  </div>
                  <div className="signup-field">
                    <label htmlFor="confirmPassword">비밀번호 확인</label>
                    <input type="password" id="confirmPassword" name="confirmPassword" value={input.confirmPassword} onChange={handleChange} maxLength={31} autoComplete="new-password" placeholder="비밀번호를 한 번 더 입력해 주세요" />
                  </div>
                </div>
              </section>
              <section className="signup-card" aria-labelledby="signup-contact-title">
                <div className="signup-section-heading"><h2 id="signup-contact-title">연락처 및 주소</h2></div>
                <div className="signup-section-fields">
                  <div className="signup-field">
                    <label htmlFor="email">이메일</label>
                    <input type="email" id="email" name="email" value={input.email} onChange={handleChange} maxLength={50} autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="example@email.com" />
                  </div>
                  <div className="signup-field">
                    <label htmlFor="phone">휴대전화</label>
                    <input type="tel" id="phone" name="phone" value={input.phone} onChange={handleChange} maxLength={11} inputMode="numeric" autoComplete="tel" placeholder="01012345678 (- 없이 입력)" />
                  </div>
                  {input.kind === '1' && <div className="signup-field">
                    <label htmlFor="phone_mom">부모님 휴대전화</label>
                    <input type="tel" id="phone_mom" name="phone_mom" value={input.phone_mom} onChange={handleChange} maxLength={11} inputMode="numeric" autoComplete="off" placeholder="01012345678 (- 없이 입력)" />
                  </div>}
                  <div className="signup-field">
                    <div className="signup-address-heading">
                      <label htmlFor="address">주소 {input.zipcode && <span className="signup-postcode">{input.zipcode}</span>}</label>
                      <Button type="button" className="signup-secondary" onClick={() => setIsModal(true)}>주소검색</Button>
                    </div>
                    <textarea id="address" name="address" value={input.address} readOnly rows={2} placeholder="주소검색으로 주소를 선택해 주세요" />
                  </div>
                  <div className="signup-field">
                    <label htmlFor="detailAddress">상세주소</label>
                    <input type="text" id="detailAddress" name="detailAddress" value={input.detailAddress} onChange={handleChange} maxLength={100} autoComplete="address-line2" placeholder="동·호수 등 상세주소를 입력해 주세요" />
                  </div>
                </div>
              </section>
              <section className="signup-card signup-terms-card" aria-labelledby="signup-terms-title">
                <div className="signup-section-heading"><h2 id="signup-terms-title">약관 동의</h2></div>
                <TermsAgreement agreedTerms={agreedTerms} agreedPrivacy={agreedPrivacy} onAgreeTermsChange={setAgreedTerms} onAgreePrivacyChange={setAgreedPrivacy} />
              </section>
              {error && <p ref={feedbackRef} tabIndex={-1} className="signup-feedback" role="alert">{error}</p>}
              {submitting && <p className="signup-progress" role="status"><span className="signup-spinner" aria-hidden="true" />회원가입을 처리하고 있습니다. 잠시만 기다려 주세요.</p>}
              <div className="signup-actions">
                <Button type="submit" className="signup-submit" disabled={submitting || checkingCode || checkingId}>{submitting ? <><span className="signup-spinner" aria-hidden="true" />가입 중…</> : '가입하기'}</Button>
                <Button type="button" className="signup-cancel" onClick={() => navigate('/login')}>취소</Button>
              </div>
            </fieldset>
          </form>
      </div>
      {isModal && <div className="signup-address-backdrop" role="dialog" aria-modal="true" aria-label="주소 검색">
        <div className="signup-address-panel"><AddressDaum setIsModal={setIsModal} handleAddressChange={handleAddressChange} /></div>
      </div>}
    </main>
  );
}
